import styled, { css, keyframes } from 'styled-components';

export const modalName = 'modal';
export const modalTimeout = 200;

/** Width over height of a printed card. */
export const CARD_ASPECT = 600 / 825;

/** Every card on screen takes its width from --card-w, which the layout around it sets. */
const cardSize = css`
  width: var(--card-w, 64px);
  aspect-ratio: ${CARD_ASPECT};
  border-radius: calc(var(--card-w, 64px) * .055);
  flex: none;
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 2px var(--accent), 0 0 6px 1px rgba(255, 203, 5, .5); }
  50% { box-shadow: 0 0 0 2px var(--accent), 0 0 14px 4px rgba(255, 203, 5, .7); }
`;

const targetable = css`
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

export const Face = styled.div`
  ${cardSize}
  position: relative;
  background: #2b2b2b center / cover no-repeat;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .55);

  ${props => props.$targetable && targetable}
  ${props => props.$dim && css`filter: saturate(.15) brightness(.6);`}
  ${props => props.$selected && css`box-shadow: 0 0 0 3px var(--accent);`}
`;

export const Slot = styled.div`
  ${cardSize}
  border: 1.5px dashed var(--line);
  display: grid;
  place-items: center;
  color: var(--accent);
  font-size: calc(var(--card-w, 64px) * .4);
  font-weight: 300;

  ${props => props.$targetable && css`
    border-color: var(--accent);
    background: rgba(255, 203, 5, .08);
    ${targetable}
  `}
`;

/** Badges keep a readable size on small cards, and grow with big ones. */
const badgeSize = 'clamp(16px, calc(var(--card-w, 64px) * .3), 28px)';

export const Damage = styled.div`
  position: absolute;
  top: calc(${badgeSize} * -.3);
  right: calc(${badgeSize} * -.3);
  min-width: ${badgeSize};
  height: ${badgeSize};
  padding: 0 4px;
  border-radius: 999px;
  background: var(--danger);
  color: #fff;
  font-weight: 700;
  font-size: calc(${badgeSize} * .5);
  display: grid;
  place-items: center;
  box-shadow: 0 0 0 1.5px rgba(0, 0, 0, .5);
`;

/** Special conditions, protection from attacks and attached Trainers, stacked across the card. */
export const StatusBadges = styled.ul`
  position: absolute;
  top: 18%;
  left: 0;
  right: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  justify-items: center;
  gap: 2px;

  li {
    padding: 1px 6px;
    border-radius: 999px;
    background: rgba(90, 40, 140, .9);
    color: #fff;
    font-size: clamp(9px, calc(var(--card-w, 64px) * .17), 13px);
    font-weight: 700;
    box-shadow: 0 0 0 1.5px rgba(0, 0, 0, .5);
  }

  li[data-kind='protection'] {
    background: rgba(20, 90, 170, .92);
  }

  li[data-kind='hindrance'],
  li[data-kind='conversion'] {
    background: rgba(160, 90, 20, .92);
  }

  li[data-kind='trainer'] {
    background: rgba(30, 120, 60, .92);
  }
`;

/** How big the attached energy symbols are on a card. */
const energySize = 'clamp(9px, calc(var(--card-w, 64px) * .17), 18px)';

export const Energies = styled.div`
  position: absolute;
  --energy-size: ${energySize};
  left: 6%;
  bottom: calc(${energySize} * -.4);
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  max-width: 90%;
`;

export const Count = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 6%;
  width: fit-content;
  margin: 0 auto;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(0, 0, 0, .75);
  color: var(--ink);
  font-size: clamp(10px, calc(var(--card-w, 64px) * .22), 14px);
  font-weight: 700;
  white-space: nowrap;
`;

export const Modal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(0, 0, 0, .7);
  opacity: 0;
  transition: opacity ease ${modalTimeout}ms;
  pointer-events: none;
  --card-w: min(80vw, 60dvh * ${CARD_ASPECT}, 420px);

  &.${modalName}-enter-active, &.${modalName}-enter-done {
    opacity: 1;
    pointer-events: auto;
  }
`;
